import { useEffect, useState } from 'preact/hooks';
import { supabase } from '../lib/supabase';
import { rows } from '../shelves/load';
import { Chip } from '../ui/bits';
import '../mybooks/my-books.css';

// From (0047, Kristi Oct 31): who gave you a gift, or where a free copy came from ("Little Free
// Library on Spokane", "Sellwood branch free bin"). Your words, never shared, like the price. A
// gift is offered the names you've used for gifts; a free copy the places you've used for free
// copies; either way you can type a new one.

export type FromHow = 'gift' | 'free';
export const hasFrom = (how: string): how is FromHow => how === 'gift' || how === 'free';

export const FROM_LABEL: Record<FromHow, string> = { gift: 'From (who gave it)', free: 'From (where you found it)' };
export const FROM_PLACEHOLDER: Record<FromHow, string> = { gift: 'A name', free: 'Little Free Library, a free bin…' };

/** Past From words for this How, most recent first, once each (ignoring case), up to 8. */
export function fromChoices(list: { got_from: string | null; updated_at: string | null }[]): string[] {
  const seen = new Map<string, string>();
  const sorted = [...list].sort((a, b) => (b.updated_at ?? '').localeCompare(a.updated_at ?? ''));
  for (const r of sorted) {
    const t = r.got_from?.trim();
    if (t && !seen.has(t.toLowerCase())) seen.set(t.toLowerCase(), t);
  }
  return [...seen.values()].slice(0, 8);
}

export async function loadFromChoices(how: FromHow): Promise<string[]> {
  const db = supabase;
  if (!db) return [];
  const list = await rows(
    db.from('copy_private').select('got_from, updated_at, copy:copies!inner(how)').eq('copy.how', how).not('got_from', 'is', null).order('updated_at', { ascending: false }).limit(200),
  );
  return fromChoices(list as { got_from: string | null; updated_at: string | null }[]);
}

/** A text box with your past names (gift) or places (free) as chips under it. */
export function FromField({ how, value, onChange, cls = 'bk-field', labelCls = 'bk-label' }: { how: FromHow; value: string; onChange: (v: string) => void; cls?: string; labelCls?: string }) {
  const [choices, setChoices] = useState<string[]>([]);
  useEffect(() => {
    let live = true;
    void loadFromChoices(how)
      .then((c) => live && setChoices(c))
      .catch(() => null);
    return () => {
      live = false;
    };
  }, [how]);
  return (
    <div class={cls}>
      <label class="bk-from">
        <span class={labelCls}>{FROM_LABEL[how]}</span>
        <input type="text" maxLength={200} placeholder={FROM_PLACEHOLDER[how]} value={value} onInput={(e) => onChange((e.target as HTMLInputElement).value)} />
      </label>
      {choices.length ? (
        <div class="oc-names" role="group" aria-label={how === 'gift' ? 'People who’ve given you books' : 'Places you’ve found free books'}>
          {choices.map((n) => (
            <Chip key={n} small plain label={n} on={value.trim().toLowerCase() === n.toLowerCase()} onToggle={() => onChange(n)} />
          ))}
        </div>
      ) : null}
      <span class="bk-dialog-sub">Only you see this.</span>
    </div>
  );
}
